import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, Box, Alert, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Accordion,
  AccordionSummary, AccordionDetails, Tooltip, LinearProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { swarmApi } from '../api';

const ACTION_COLORS = {
  scale_up:     'success',
  scale_down:   'warning',
  rollback:     'error',
  restart:      'warning',
  prune_images: 'info',
  no_action:    'default',
};

function ConfidenceBar({ value }) {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? 'success' : pct >= 60 ? 'warning' : 'error';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress
        variant="determinate" value={pct}
        color={color}
        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
      />
      <Typography variant="caption">{pct}%</Typography>
    </Box>
  );
}

function AiGovernance({ user }) {
  const [recs, setRecs] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewDialog, setReviewDialog] = useState({ open: false, rec: null });
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // New state variables for editable settings and stats
  const [stats, setStats] = useState(null);
  const [settingsEditing, setSettingsEditing] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recRes, decRes, setRes, statRes] = await Promise.all([
        swarmApi.listAiRecommendations().catch(() => ({ data: [] })),
        swarmApi.listAiDecisions().catch(() => ({ data: [] })),
        isAdmin ? swarmApi.getAiSettings().catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        isAdmin ? swarmApi.getAiStats().catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setRecs(recRes.data);
      setDecisions(decRes.data);
      setSettings(setRes.data);
      setStats(statRes.data);
      setError('');
    } catch {
      setError('Failed to load AI governance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleReview = async (decision) => {
    if (!reviewDialog.rec) return;
    setSubmitting(true);
    try {
      await swarmApi.reviewAiRecommendation(reviewDialog.rec.id, decision, reviewComment);
      setReviewDialog({ open: false, rec: null });
      setReviewComment('');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Review failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await swarmApi.updateAiSettings(settingsDraft);
      setSettingsEditing(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  if (loading) return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;

  const pendingRecs = recs.filter(r => r.status === 'pending_review');
  const shadowRecs  = recs.filter(r => r.status === 'shadow_only');

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <SmartToyIcon sx={{ mr: 1, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>AI Governance</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Settings & Stats Panel */}
      {settings && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>⚙️ AI Settings</Typography>
            {isAdmin && !settingsEditing && (
              <Button size="small" onClick={() => { setSettingsDraft({ autonomyTier: settings.autonomyTier, aiEnabled: settings.aiEnabled, maxAutonomousActionsPerHour: settings.maxAutonomousActionsPerHour, evaluationCooldownMinutes: settings.evaluationCooldownMinutes }); setSettingsEditing(true); }}>
                Edit
              </Button>
            )}
            {isAdmin && settingsEditing && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" onClick={handleSaveSettings} disabled={settingsSaving}>
                  {settingsSaving ? 'Saving…' : 'Save'}
                </Button>
                <Button size="small" onClick={() => setSettingsEditing(false)}>Cancel</Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* Kill Switch */}
            <Box>
              <Typography variant="caption" color="text.secondary">AI Kill Switch</Typography>
              {settingsEditing ? (
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Button size="small" variant={settingsDraft.aiEnabled ? 'contained' : 'outlined'} color="success" onClick={() => setSettingsDraft(d => ({ ...d, aiEnabled: true }))}>Enabled</Button>
                  <Button size="small" variant={!settingsDraft.aiEnabled ? 'contained' : 'outlined'} color="error" onClick={() => setSettingsDraft(d => ({ ...d, aiEnabled: false }))}>Disabled</Button>
                </Box>
              ) : (
                <Chip label={settings.aiEnabled ? '✓ AI Enabled' : '✗ AI Disabled'} color={settings.aiEnabled ? 'success' : 'error'} size="small" sx={{ display: 'block', mt: 0.5 }} />
              )}
            </Box>

            {/* Autonomy Tier */}
            <Box>
              <Typography variant="caption" color="text.secondary">Autonomy Tier</Typography>
              {settingsEditing ? (
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  {['shadow', 'human_review', 'autonomous'].map(t => (
                    <Button key={t} size="small"
                      variant={settingsDraft.autonomyTier === t ? 'contained' : 'outlined'}
                      color={t === 'autonomous' ? 'error' : t === 'human_review' ? 'warning' : 'default'}
                      onClick={() => setSettingsDraft(d => ({ ...d, autonomyTier: t }))}>
                      {t.replace('_', ' ')}
                    </Button>
                  ))}
                </Box>
              ) : (
                <Chip
                  label={settings.autonomyTier.replace('_', ' ').toUpperCase()}
                  color={settings.autonomyTier === 'autonomous' ? 'error' : settings.autonomyTier === 'human_review' ? 'warning' : 'default'}
                  sx={{ display: 'block', mt: 0.5 }}
                />
              )}
            </Box>

            {/* Rate Cap */}
            <Box>
              <Typography variant="caption" color="text.secondary">Max Autonomous/Hour</Typography>
              {settingsEditing ? (
                <TextField type="number" size="small" value={settingsDraft.maxAutonomousActionsPerHour ?? ''} inputProps={{ min: 1, max: 100 }}
                  onChange={e => setSettingsDraft(d => ({ ...d, maxAutonomousActionsPerHour: parseInt(e.target.value) || 5 }))}
                  sx={{ width: 90, mt: 0.5, display: 'block' }} />
              ) : (
                <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>{settings.maxAutonomousActionsPerHour}</Typography>
              )}
            </Box>

            {/* Cooldown */}
            <Box>
              <Typography variant="caption" color="text.secondary">Cooldown (min)</Typography>
              {settingsEditing ? (
                <TextField type="number" size="small" value={settingsDraft.evaluationCooldownMinutes ?? ''} inputProps={{ min: 1, max: 120 }}
                  onChange={e => setSettingsDraft(d => ({ ...d, evaluationCooldownMinutes: parseInt(e.target.value) || 10 }))}
                  sx={{ width: 90, mt: 0.5, display: 'block' }} />
              ) : (
                <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>{settings.evaluationCooldownMinutes} min</Typography>
              )}
            </Box>

            {/* Model / API Key */}
            <Box>
              <Typography variant="caption" color="text.secondary">Model</Typography>
              <Typography variant="body2" fontWeight="bold">{settings.model}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">API Key</Typography>
              <Chip label={settings.hasApiKey ? '✓ Configured' : '✗ Missing'} color={settings.hasApiKey ? 'success' : 'error'} size="small" sx={{ display: 'block', mt: 0.5 }} />
            </Box>
          </Box>

          {/* Cost Stats */}
          {stats && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Last 24h — Evaluations</Typography>
                <Typography variant="h6">{stats.last24Hours.evaluations}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Last 24h — Tokens</Typography>
                <Typography variant="h6">{(stats.last24Hours.totalPromptTokens + stats.last24Hours.totalCompletionTokens).toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Last 24h — Est. Cost</Typography>
                <Typography variant="h6" color={stats.last24Hours.estimatedCostUsd > 1 ? 'warning.main' : 'text.primary'}>
                  ${stats.last24Hours.estimatedCostUsd.toFixed(4)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">7-Day — Est. Cost</Typography>
                <Typography variant="h6">${stats.last7Days.estimatedCostUsd.toFixed(4)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Autonomous (Last Hour)</Typography>
                <Chip
                  label={`${stats.autonomousActionsLast1Hour} / ${stats.rateCap} cap`}
                  color={stats.autonomousActionsLast1Hour >= stats.rateCap ? 'error' : stats.autonomousActionsLast1Hour >= stats.rateCap * 0.8 ? 'warning' : 'success'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Pending Human Review */}
      {pendingRecs.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{pendingRecs.length} recommendation{pendingRecs.length > 1 ? 's' : ''} awaiting your review.</strong>
          </Alert>
          {pendingRecs.map(rec => (
            <Paper key={rec.id} sx={{ mb: 2, p: 2, border: '2px solid', borderColor: 'warning.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip label={rec.actionType.replace('_', ' ')} color={ACTION_COLORS[rec.actionType] || 'default'} sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>{rec.serviceName}</Typography>
                {rec.targetReplicas && <Typography variant="body2" color="text.secondary">→ {rec.targetReplicas} replicas</Typography>}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">AI Confidence</Typography>
                <ConfidenceBar value={rec.confidence} />
              </Box>
              <Accordion sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">View full debate reasoning</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="subtitle2" color="success.main">🐂 Advocate</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>{rec.bullArgument}</Typography>
                  <Typography variant="subtitle2" color="error.main">🐻 Critic</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>{rec.bearArgument}</Typography>
                  <Typography variant="subtitle2" color="primary.main">⚖️ Arbiter</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>{rec.arbiterReasoning}</Typography>
                  {rec.incidentSummary && (
                    <>
                      <Typography variant="subtitle2" color="info.main" sx={{ mt: 1 }}>🔍 Incident Summary</Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{rec.incidentSummary}</Typography>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
              {isAdmin && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 1 }}>
                  <Button startIcon={<ThumbUpIcon />} variant="contained" color="success"
                    onClick={() => { setReviewDialog({ open: true, rec }); }}>
                    Approve &amp; Execute
                  </Button>
                  <Button startIcon={<ThumbDownIcon />} variant="outlined" color="error"
                    onClick={() => { setReviewDialog({ open: true, rec, reject: true }); }}>
                    Reject
                  </Button>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                Expires: {new Date(rec.expiresAt).toLocaleString()}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* Shadow Log */}
      {shadowRecs.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Shadow Mode Log (informational)</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Incident Summary / Reasoning</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shadowRecs.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.serviceName}</TableCell>
                    <TableCell><Chip label={r.actionType.replace('_', ' ')} size="small" color={ACTION_COLORS[r.actionType] || 'default'} /></TableCell>
                    <TableCell><ConfidenceBar value={r.confidence} /></TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip title={r.arbiterReasoning}>
                        <Box>
                          {r.incidentSummary ? (
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>{r.incidentSummary}</Typography>
                          ) : null}
                          <Typography variant="caption" color="text.secondary" noWrap>{r.arbiterReasoning}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Decision Audit Log */}
      <Typography variant="h6" gutterBottom>Executed Decisions (Audit Log)</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Tokens / Cost</TableCell>
              <TableCell>Executed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {decisions.map(d => (
              <TableRow key={d.id} hover>
                <TableCell>{d.serviceName}</TableCell>
                <TableCell><Chip label={d.actionType.replace('_', ' ')} size="small" color={ACTION_COLORS[d.actionType] || 'default'} /></TableCell>
                <TableCell><Chip label={d.executionMode.replace('_', ' ')} size="small" variant="outlined" /></TableCell>
                <TableCell>{d.executedBy}</TableCell>
                <TableCell>
                  <Chip label={d.succeeded ? 'Success' : 'Failed'} size="small" color={d.succeeded ? 'success' : 'error'} />
                </TableCell>
                <TableCell>
                  {d.promptTokens || d.completionTokens ? (
                    <Typography variant="caption">
                      {d.promptTokens + d.completionTokens} (${d.estimatedCostUsd ? d.estimatedCostUsd.toFixed(4) : '0.0000'})
                    </Typography>
                  ) : '-'}
                </TableCell>
                <TableCell>{new Date(d.executedAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {decisions.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">
                <Typography color="text.secondary" sx={{ py: 2 }}>No decisions executed yet.</Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onClose={() => setReviewDialog({ open: false, rec: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{reviewDialog.reject ? 'Reject Recommendation' : 'Approve & Execute'}</DialogTitle>
        <DialogContent>
          {reviewDialog.rec && (
            <Box>
              <Alert severity={reviewDialog.reject ? 'warning' : 'info'} sx={{ mb: 2 }}>
                {reviewDialog.reject
                  ? `Rejecting: ${reviewDialog.rec.actionType} on ${reviewDialog.rec.serviceName}`
                  : `Approving: ${reviewDialog.rec.actionType} on ${reviewDialog.rec.serviceName}. This will execute immediately.`}
              </Alert>
              <TextField
                label="Comment (optional)"
                fullWidth multiline rows={2}
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog({ open: false, rec: null })}>Cancel</Button>
          <Button
            variant="contained"
            color={reviewDialog.reject ? 'error' : 'success'}
            disabled={submitting}
            onClick={() => handleReview(reviewDialog.reject ? 'rejected' : 'approved')}
          >
            {submitting ? 'Processing...' : reviewDialog.reject ? 'Confirm Reject' : 'Confirm & Execute'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AiGovernance;
